import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAdminSettings, updateAdminSettingsSection } from "../../../api/adminSettings";

const DEFAULT_SETTINGS = {
  business: {
    companyProfile: {
      businessName: "Wayli",
      legalEntityType: "GmbH",
      contactEmail: "hello@wayli.at",
      contactPhone: "+43 1 234 56 78",
      hoursDefault: "08:00–20:00",
    },
    taxVat: {
      vatEnabled: true,
      vatRatePercent: "20",
      vatIncluded: true,
      taxCategory: "standard",
    },
    currencyLocale: {
      currency: "EUR",
      language: "EN",
      timezone: "Europe/Vienna",
    },
  },
  booking: {
    availability: {
      bookingsEnabled: true,
    },
    window: {
      minNoticeMinutes: "30",
      maxAdvanceDays: "30",
      autoExpireMinutes: "15",
      seasonStartDate: "",
      seasonEndDate: "",
    },
    capacityHandling: {
      allowOverbooking: false,
      holdInventory: true,
    },
    lateCheckinPolicy: {
      graceMinutes: "15",
      intervalMinutes: "15",
      feePerInterval: "5",
    },
    cancellationPolicy: {
      freeCancellationHours: "24",
      lateCancellationFee: "50% of booking",
      noShowPolicy: "Charge full amount + flag",
    },
    qrRules: {
      pickupRequiresScan: true,
      returnRequiresScan: false,
      allowManualOverride: true,
    },
  },
  pricing: {
    basePricing: {
      rows: [
        { name: "SUP", firstHour: "", nextHalfHour: "", fullDay: "50", weekly: "300", trip: "" },
        { name: "Kayak", firstHour: "", nextHalfHour: "", fullDay: "60", weekly: "360", trip: "" },
      ],
    },
    discounts: {
      codeName: "SUMMER20",
      discount: "20%",
      validity: "May 1 – Sep 30",
      usageLimit: "500",
    },
    promoCodes: [],
    deposits: {
      required: false,
      amount: "50",
      refundRule: "auto",
    },
  },
  locations: {
    locationSettings: {
      name: "Harbor Base",
      address: "10 Dock Street",
      operatingHours: "Mon–Sun 08:00–20:00",
      pickupInstructions: "Pick up at the café counter, show QR.",
    },
    partnerProfiles: {
      legalName: "Harbor Cafe GmbH",
      contactPerson: "Maria Huber",
      iban: "AT12 3456 7890 1234",
      payoutSchedule: "monthly",
    },
    commissionModel: {
      model: "percentage",
      rate: "12%",
    },
    equipmentAssignment: {
      stockModel: "pool",
      assignedUnits: "18 SUP / 12 Kayak",
    },
    partnerAccess: {
      canSeeOwnBookings: true,
      limitedActions: true,
    },
    partnerReadiness: {
      items: [
        "Hours set",
        "Payout info completed",
        "Pickup instructions added",
        "Equipment assigned",
      ],
    },
  },
  payments: {
    availability: {
      paymentsEnabled: true,
    },
    provider: {
      provider: "stripe",
      environment: "Test",
      apiKeyMasked: "••••••••••",
      webhookStatus: "Last received: 5m ago",
    },
    refundRules: {
      defaultPolicy: "bookingRules",
      manualRefundRoles: "owner",
      reasonRequired: true,
    },
    payouts: {
      schedule: "monthly",
      autoCalcCommission: true,
    },
    invoices: {
      receiptTemplate: "Default receipt",
      invoicePrefix: "WAYLI-2025-",
      pdfEnabled: true,
    },
  },
  notifications: {
    customerEmails: {
      bookingConfirmation: true,
      paymentReceipt: true,
      reminders: true,
    },
    partnerNotifications: {
      newBookingAlert: true,
      returnOverdueAlert: true,
    },
    adminAlerts: {
      bookingCreated: true,
      bookingRecipientEmail: "",
      paymentFailures: true,
      unscannedPickups: true,
      lateReturns: true,
    },
  },
  branding: {
    brandAssets: {
      primaryColor: "#1E3B3A",
      accentColor: "#C5A25E",
    },
    seoTracking: {
      googleAnalyticsId: "",
      metaPixelId: "",
      cookieBannerEnabled: true,
    },
    publicContent: {
      faq: "Add a short FAQ entry here.",
      termsLink: "/legal/terms",
      emergencyContact: "+43 800 123 456",
    },
  },
  security: {
    loginSecurity: {
      requireTwoFactor: true,
      sessionTimeout: "12h",
      allowedDomains: "",
    },
    adminAccessPin: {
      enabled: false,
      pin: "",
    },
  },
  integrations: {
    calendarExport: {
      enableIcal: false,
      icalUrl: "https://wayli.at/ical",
    },
    accountingExport: {
      csvFormat: "accountant",
      monthlyPack: true,
    },
    maps: {
      googleMapsApiKey: "",
    },
    webhooks: {
      bookingCreated: true,
      bookingPaid: true,
      bookingCompleted: true,
      bookingRefunded: true,
    },
  },
  audit: {
    auditLog: {
      logSensitiveEdits: true,
      retentionDays: "365",
    },
    systemHealth: {
      services: [
        { name: "Payment webhooks", status: "Healthy · last event 5m ago" },
        { name: "Email provider", status: "Healthy · 99.9% uptime" },
        { name: "QR scan endpoint", status: "Healthy · 140 ms avg" },
      ],
    },
  },
};

const cloneSettings = (value) => {
  if (Array.isArray(value)) {
    return value.map(cloneSettings);
  }
  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = cloneSettings(value[key]);
      return acc;
    }, {});
  }
  return value;
};

const mergeSettings = (base, incoming) => {
  if (Array.isArray(base)) {
    if (Array.isArray(incoming)) {
      return incoming.map(cloneSettings);
    }
    return base.map(cloneSettings);
  }
  if (!base || typeof base !== "object") {
    return incoming !== undefined ? incoming : base;
  }
  const incomingObject =
    incoming && typeof incoming === "object" && !Array.isArray(incoming) ? incoming : {};
  const result = {};
  Object.keys(base).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(incomingObject, key)) {
      result[key] = mergeSettings(base[key], incomingObject[key]);
    } else {
      result[key] = cloneSettings(base[key]);
    }
  });
  Object.keys(incomingObject).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(result, key)) {
      result[key] = cloneSettings(incomingObject[key]);
    }
  });
  return result;
};

const normalizePricingRow = (row) => {
  if (!row || typeof row !== "object") {
      return {
        name: "Equipment",
        firstHour: "",
        nextHalfHour: "",
        fullDay: "",
        weekly: "",
        trip: "",
      };
  }
  const {
    twoHours,
    fourHours,
    equipmentTypeId,
    equipmentTypeName,
    id,
    name,
    firstHour,
    nextHalfHour,
    fullDay,
    weekly,
    trip,
    ...rest
  } = row;
      const normalized = {
        ...rest,
        name: name || equipmentTypeName || "Equipment",
        firstHour: firstHour ?? twoHours ?? "",
        nextHalfHour: nextHalfHour ?? fourHours ?? "",
        fullDay: fullDay ?? "",
        weekly: weekly ?? "",
        trip: trip ?? "",
      };
  const resolvedId = id ?? equipmentTypeId;
  if (resolvedId !== null && resolvedId !== undefined) {
    normalized.id = resolvedId;
  }
  return normalized;
};

const buildBasePricingRows = (rows, equipmentSnapshot) => {
  const normalizedRows = (Array.isArray(rows) ? rows : []).map(normalizePricingRow);
  const equipment = Array.isArray(equipmentSnapshot) ? equipmentSnapshot : [];
  const rowsById = new Map();
  const rowsByName = new Map();

  normalizedRows.forEach((row) => {
    if (row.id !== null && row.id !== undefined) {
      rowsById.set(String(row.id), row);
    }
    if (row.name) {
      rowsByName.set(row.name.toLowerCase(), row);
    }
  });

  const equipmentRows = equipment.map((item) => {
    const id = item.id ?? item.equipmentTypeId ?? null;
    const name = item.name || item.equipmentTypeName || "Equipment";
    const match =
      (id !== null && id !== undefined && rowsById.get(String(id))) ||
      (name && rowsByName.get(name.toLowerCase()));
    if (match) {
      const merged = {
        ...match,
        name: name || match.name || "Equipment",
        firstHour: match.firstHour ?? "",
        nextHalfHour: match.nextHalfHour ?? "",
        fullDay: match.fullDay ?? "",
        weekly: match.weekly ?? "",
        trip: match.trip ?? "",
      };
      const resolvedId = id ?? match.id;
      if (resolvedId !== null && resolvedId !== undefined) {
        merged.id = resolvedId;
      }
      return merged;
    }
    const created = {
      name,
      firstHour: "",
      nextHalfHour: "",
      fullDay: "",
      weekly: "",
      trip: "",
    };
    if (id !== null && id !== undefined) {
      created.id = id;
    }
    return created;
  });

  const usedKeys = new Set(
    equipmentRows.map((row) =>
      row.id !== null && row.id !== undefined
        ? `id:${row.id}`
        : `name:${(row.name || "").toLowerCase()}`
    )
  );

  const extraRows = normalizedRows.filter((row) => {
    const key =
      row.id !== null && row.id !== undefined
        ? `id:${row.id}`
        : `name:${(row.name || "").toLowerCase()}`;
    return !usedKeys.has(key);
  });

  return [...equipmentRows, ...extraRows];
};

const applyPricingRows = (settings, equipmentSnapshot) => {
  if (!settings || typeof settings !== "object") return settings;
  const pricing = settings.pricing || {};
  const basePricing = pricing.basePricing || {};
  const currentRows = Array.isArray(basePricing.rows) ? basePricing.rows : [];
  const nextRows = buildBasePricingRows(currentRows, equipmentSnapshot);
  if (JSON.stringify(currentRows) === JSON.stringify(nextRows)) {
    return settings;
  }
  return {
    ...settings,
    pricing: {
      ...pricing,
      basePricing: {
        ...basePricing,
        rows: nextRows,
      },
    },
  };
};

const useAdminSettings = ({ showFeedback, refreshAudit, equipmentSnapshot }) => {
  const [settingsData, setSettingsData] = useState(() => cloneSettings(DEFAULT_SETTINGS));
  const [settingsInitial, setSettingsInitial] = useState(() =>
    cloneSettings(DEFAULT_SETTINGS)
  );
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsSavingSection, setSettingsSavingSection] = useState(null);
  const [settingsSaveState, setSettingsSaveState] = useState({});
  const [settingsActiveSection, setSettingsActiveSection] = useState("booking");
  const [settingsHighlightId, setSettingsHighlightId] = useState(null);
  const saveTimersRef = useRef({});

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      setSettingsLoading(true);
      setSettingsError("");
      try {
        const payload = await getAdminSettings();
        if (cancelled) return;
        const merged = mergeSettings(DEFAULT_SETTINGS, payload || {});
        const normalized = applyPricingRows(merged);
        setSettingsData(normalized);
        setSettingsInitial(cloneSettings(normalized));
      } catch (error) {
        if (cancelled) return;
        setSettingsError(error?.message || "Failed to load settings.");
        const fallback = applyPricingRows(DEFAULT_SETTINGS);
        setSettingsData(cloneSettings(fallback));
        setSettingsInitial(cloneSettings(fallback));
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach((timer) => {
        clearTimeout(timer);
      });
      saveTimersRef.current = {};
    };
  }, []);

  const settingsDirtySections = useMemo(() => {
    const next = {};
    Object.keys(settingsData || {}).forEach((sectionId) => {
      const current = settingsData?.[sectionId];
      const initial = settingsInitial?.[sectionId];
      next[sectionId] = JSON.stringify(current) !== JSON.stringify(initial);
    });
    return next;
  }, [settingsData, settingsInitial]);

  useEffect(() => {
    setSettingsData((prev) => applyPricingRows(prev, equipmentSnapshot));
    setSettingsInitial((prev) => {
      if (settingsDirtySections.pricing) return prev;
      return applyPricingRows(prev, equipmentSnapshot);
    });
  }, [equipmentSnapshot, settingsDirtySections.pricing]);

  const updateSettingsValue = useCallback((sectionId, path, value) => {
    setSettingsData((prev) => {
      const next = cloneSettings(prev);
      if (!next[sectionId]) {
        next[sectionId] = {};
      }
      let cursor = next[sectionId];
      for (let i = 0; i < path.length - 1; i += 1) {
        const key = path[i];
        if (!cursor[key] || typeof cursor[key] !== "object") {
          cursor[key] = typeof path[i + 1] === "number" ? [] : {};
        }
        cursor = cursor[key];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  }, []);

  const handleSettingsSave = useCallback(
    async (sectionId, payloadOverride = null) => {
      if (!sectionId) return;
      const payload = payloadOverride ?? (settingsData?.[sectionId] || {});
      setSettingsSavingSection(sectionId);
      setSettingsSaveState((prev) => ({
        ...prev,
        [sectionId]: { status: "saving" },
      }));

      try {
        const response = await updateAdminSettingsSection(sectionId, payload);
        const mergedSection = mergeSettings(
          DEFAULT_SETTINGS[sectionId] || {},
          response || {}
        );
        const normalizedSection =
          sectionId === "pricing"
            ? applyPricingRows({ pricing: mergedSection }, equipmentSnapshot).pricing ||
              mergedSection
            : mergedSection;
        setSettingsData((prev) => ({
          ...prev,
          [sectionId]: normalizedSection,
        }));
        setSettingsInitial((prev) => ({
          ...prev,
          [sectionId]: cloneSettings(normalizedSection),
        }));
        setSettingsSaveState((prev) => ({
          ...prev,
          [sectionId]: { status: "saved" },
        }));
        if (saveTimersRef.current[sectionId]) {
          clearTimeout(saveTimersRef.current[sectionId]);
        }
        saveTimersRef.current[sectionId] = setTimeout(() => {
          setSettingsSaveState((prev) => {
            const next = { ...prev };
            delete next[sectionId];
            return next;
          });
        }, 2400);
        showFeedback("Settings saved.", "success");
        await refreshAudit({ silent: true });
      } catch (error) {
        setSettingsSaveState((prev) => ({
          ...prev,
          [sectionId]: {
            status: "error",
            message: error?.message || "Failed to save settings.",
          },
        }));
        showFeedback(error?.message || "Failed to save settings.", "warning");
      } finally {
        setSettingsSavingSection(null);
      }
    },
    [equipmentSnapshot, refreshAudit, settingsData, showFeedback]
  );

  const handleSettingsCancel = useCallback(
    (sectionId) => {
      if (!sectionId) return;
      const fallback = cloneSettings(DEFAULT_SETTINGS[sectionId] || {});
      const initial = settingsInitial?.[sectionId]
        ? cloneSettings(settingsInitial[sectionId])
        : fallback;
      setSettingsData((prev) => ({
        ...prev,
        [sectionId]: initial,
      }));
      setSettingsSaveState((prev) => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      showFeedback("Changes discarded.", "info");
    },
    [settingsInitial, showFeedback]
  );

  const handleSettingsTestAction = (label) => {
    showFeedback(`${label} test queued.`, "info");
  };

  const handleSettingsResetAction = (label) => {
    showFeedback(`${label} reset queued.`, "warning");
  };

  return {
    settingsData,
    settingsLoading,
    settingsError,
    settingsSavingSection,
    settingsSaveState,
    settingsActiveSection,
    setSettingsActiveSection,
    settingsHighlightId,
    setSettingsHighlightId,
    settingsDirtySections,
    updateSettingsValue,
    handleSettingsSave,
    handleSettingsCancel,
    handleSettingsTestAction,
    handleSettingsResetAction,
  };
};

export default useAdminSettings;
